/**
 * Format instants for the UI using an explicit IANA timezone (e.g. Display Preferences).
 */

const DEFAULT_TZ = () =>
  typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    : "UTC";

function toDate(value: Date | string): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  return parseStatisticsChartInstant(String(value));
}

/** Date + time (matches prior Trade Log table style). */
export function formatTradeDateTimeForDisplay(
  value: Date | string,
  timeZone: string = DEFAULT_TZ()
): string {
  const d = toDate(value);
  if (!d) return "—";
  const zone = timeZone?.trim() || DEFAULT_TZ();
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  }
}

/** Date + time (compact, used in trade details). */
export function formatTradeDateTimeDetailForDisplay(
  value: Date | string,
  timeZone: string = DEFAULT_TZ()
): string {
  const d = toDate(value);
  if (!d) return "—";
  const zone = timeZone?.trim() || DEFAULT_TZ();
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}

/** Calendar date only (e.g. Recent Trades cards). */
/** 24h datetime for CSV (explicit IANA zone). */
export function formatTradeDateTimeCsvInZone(
  value: Date | string | undefined | null,
  timeZone: string = DEFAULT_TZ()
): string {
  if (value == null || value === "") return "";
  const d = toDate(value as Date | string);
  if (!d) return "";
  const zone = timeZone?.trim() || DEFAULT_TZ();
  try {
    return new Intl.DateTimeFormat("sv-SE", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    })
      .format(d)
      .replace(" ", "T");
  } catch {
    return d.toISOString();
  }
}

export function formatTradeDateOnlyForDisplay(
  value: Date | string,
  timeZone: string = DEFAULT_TZ()
): string {
  const d = toDate(value);
  if (!d) return "—";
  const zone = timeZone?.trim() || DEFAULT_TZ();
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(d);
  }
}

/**
 * Parse statistics/chart API timestamps. `YYYY-MM-DD HH:mm` without offset is treated as UTC
 * (matches typical SQL→JSON exports). `YYYY-MM-DDTHH:mm` without offset is treated as UTC so
 * labels match trade log when the browser is not in the account timezone.
 */
export function parseStatisticsChartInstant(raw: string): Date | null {
  const s = raw.trim();
  if (!s) return null;
  if (s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) {
    const d = new Date(s.replace(" ", "T") + "Z");
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
    const d = new Date(s.endsWith("Z") ? s : `${s}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T12:00:00Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** X-axis / tooltip hour in account (or device) timezone, 12h. */
export function formatChartHour12(value: Date | string, timeZone: string = DEFAULT_TZ()): string {
  const d = typeof value === "string" ? parseStatisticsChartInstant(value) : toDate(value);
  if (!d) return typeof value === "string" ? value : "—";
  const zone = timeZone?.trim() || DEFAULT_TZ();
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hour: "numeric",
      hour12: true,
    }).format(d);
  } catch {
    return d.toLocaleTimeString("en-US", { hour: "numeric", hour12: true });
  }
}

/**
 * Hourly chart axis label. Same-day series → "3 PM"; multi-day → "Jul 23, 3 PM".
 */
export function formatChartHourAxisLabel(
  value: Date | string,
  timeZone: string = DEFAULT_TZ(),
  options?: { includeDay?: boolean }
): string {
  const hour = formatChartHour12(value, timeZone);
  if (!options?.includeDay) return hour;

  const d = typeof value === "string" ? parseStatisticsChartInstant(value) : toDate(value);
  if (!d) return hour;
  const zone = timeZone?.trim() || DEFAULT_TZ();
  try {
    const day = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      month: "short",
      day: "numeric",
    }).format(d);
    return `${day}, ${hour}`;
  } catch {
    return hour;
  }
}

export type ChartBucketFormatOptions = {
  /** When true, appends calendar year (e.g. "Mar 24, 2021"). */
  includeYear?: boolean;
};

/** Parse `YYYY-MM-DD` (or longer strings with a date prefix). */
export function parseYmdBucket(ymd: string): { year: number; month: number; day: number } | null {
  const key = ymd.trim().slice(0, 10);
  const t = key.split("-");
  if (t.length !== 3) return null;
  const year = parseInt(t[0], 10);
  const month = parseInt(t[1], 10);
  const day = parseInt(t[2], 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  return { year, month, day };
}

/** Parse `YYYY-MM`. */
export function parseYmBucket(ym: string): { year: number; month: number } | null {
  const t = ym.trim().slice(0, 7).split("-");
  if (t.length !== 2) return null;
  const year = parseInt(t[0], 10);
  const month = parseInt(t[1], 10);
  if (Number.isNaN(year) || Number.isNaN(month)) return null;
  return { year, month };
}

/** Extract calendar year from a daily (`YYYY-MM-DD`) or monthly (`YYYY-MM`) chart bucket. */
export function chartBucketYear(bucket: string): number | null {
  const trimmed = bucket.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return parseYmdBucket(trimmed)?.year ?? null;
  if (/^\d{4}-\d{2}$/.test(trimmed)) return parseYmBucket(trimmed)?.year ?? null;
  return null;
}

/**
 * Whether chart X-axis / tooltips should show the year on daily buckets.
 * True when the series spans more than one calendar year.
 */
export function shouldIncludeYearOnChartAxis(buckets: string[]): boolean {
  const years = new Set<number>();
  for (const b of buckets) {
    const y = chartBucketYear(b);
    if (y != null) years.add(y);
  }
  return years.size > 1;
}

/** `YYYY-MM-DD` … `YYYY-MM-DD` range label for weekly (or similar) chart buckets. */
export function formatYmdRangeShortInZone(
  startYmd: string,
  endYmd: string,
  timeZone: string = DEFAULT_TZ(),
  options?: ChartBucketFormatOptions
): string {
  const startLabel = formatYmdBucketShortInZone(startYmd, timeZone, options);
  const endLabel = formatYmdBucketShortInZone(endYmd, timeZone, options);
  return `${startLabel} - ${endLabel}`;
}

/** All `YYYY-MM` buckets from `startYm` through `endYm`, inclusive. */
export function enumerateYmBucketsInclusive(startYm: string, endYm: string): string[] {
  const start = parseYmBucket(startYm);
  const end = parseYmBucket(endYm);
  if (!start || !end) return [];
  const out: string[] = [];
  let y = start.year;
  let m = start.month;
  while (y < end.year || (y === end.year && m <= end.month)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/** Calendar day bucket `YYYY-MM-DD` → short month+day label in `timeZone`. */
export function formatYmdBucketShortInZone(
  ymd: string,
  timeZone: string = DEFAULT_TZ(),
  options?: ChartBucketFormatOptions
): string {
  const t = ymd.trim().split("-");
  if (t.length !== 3) return ymd;
  const y = parseInt(t[0], 10);
  const mo = parseInt(t[1], 10);
  const d = parseInt(t[2], 10);
  if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return ymd;
  const dObj = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0, 0));
  const zone = timeZone?.trim() || DEFAULT_TZ();
  const includeYear = options?.includeYear === true;
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      month: "short",
      day: "numeric",
      ...(includeYear ? { year: "numeric" } : {}),
    }).format(dObj);
  } catch {
    return ymd;
  }
}

/** Month bucket `YYYY-MM` → "Apr 2026" style in `timeZone`. */
export function formatYmBucketShortInZone(ym: string, timeZone: string = DEFAULT_TZ()): string {
  const t = ym.trim().split("-");
  if (t.length !== 2) return ym;
  const y = parseInt(t[0], 10);
  const mo = parseInt(t[1], 10);
  if (Number.isNaN(y) || Number.isNaN(mo)) return ym;
  const dObj = new Date(Date.UTC(y, mo - 1, 15, 12, 0, 0, 0));
  const zone = timeZone?.trim() || DEFAULT_TZ();
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      month: "short",
      year: "numeric",
    }).format(dObj);
  } catch {
    return ym;
  }
}

/** Month bucket `YYYY-MM` → "April 2026" in `timeZone` (tables / long labels). */
export function formatYmBucketLongInZone(ym: string, timeZone: string = DEFAULT_TZ()): string {
  const t = ym.trim().split("-");
  if (t.length !== 2) return ym;
  const y = parseInt(t[0], 10);
  const mo = parseInt(t[1], 10);
  if (Number.isNaN(y) || Number.isNaN(mo)) return ym;
  const dObj = new Date(Date.UTC(y, mo - 1, 15, 12, 0, 0, 0));
  const zone = timeZone?.trim() || DEFAULT_TZ();
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      month: "long",
      year: "numeric",
    }).format(dObj);
  } catch {
    return ym;
  }
}

/**
 * Label for a backend **UTC hour bucket** (0–23): wall-clock time in `timeZone` at that UTC hour
 * (anchor mid-year to reduce DST edge weirdness). Use when API buckets are UTC but UI should read in account TZ.
 */
export function formatUtcHourWallLabel(utcHour0to23: number, timeZone: string = DEFAULT_TZ()): string {
  const h = ((Math.floor(utcHour0to23) % 24) + 24) % 24;
  const instant = new Date(Date.UTC(2020, 5, 15, h, 0, 0, 0));
  return formatChartHour12(instant, timeZone);
}
